import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoShortAuthors,
  mlaDate,
  naturalName,
  type PartialDate,
  type PersonName,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import { readDate } from "@/lib/tool-kit/citation-sources-books/common";
import {
  closer,
  get,
  italic,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  andList,
  fullUrl,
  longDate,
  type MediaLabels,
  type MediaValues,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";

/*
 * A vital record (birth, death or marriage certificate) or a census schedule, in
 * Chicago 18, MLA 9 and APA 7.
 *
 * Sources checked (October 2026). None of the three manuals prints a vital-record or
 * census-schedule model. Genealogists and historians follow Chicago's note logic as
 * Elizabeth Shown Mills sets it out in Evidence Explained, so the Chicago output is the
 * firmest; the MLA and APA output applies each style's pattern for an unpublished or
 * archival document, and says so in its note.
 * - Chicago / Evidence Explained: the census full note and source-list entry follow the
 *   Evidence Explained site's 1940 census image model — "1940 U.S. census, ___ County,
 *   ___ [State], [Location], population schedule, enumeration district (ED) ___, page
 *   __ (stamped) or sheet __ (handwritten), dwelling __, family ___, line ____, [Name
 *   of Person]; digital image, Archives.gov (http://1940census.archives.gov: accessed
 *   ___); citing NARA microfilm publication T627, roll ___." and "[State]. [County].
 *   1940 US census, population schedule. Digital images, Archives.gov." — with the
 *   short note "1940 US census, [County], [State]. pop. sch., p. ____ (stamped), dwell.
 *   ____, fam. ___, [Name of Person]." Dates are written Chicago's way, not Evidence
 *   Explained's day-month-year. A certificate follows the Chicago-footnote genealogy
 *   guide at Hanover College: "Eliza Calhun, death certificate, 4 Aug. 1930, file no.
 *   40076, Texas State Department of Health, copy in possession of author." and, for a
 *   commercial site, the record, "digital image s.v.", and the site.
 * - MLA 9 and APA 7: the archival-document patterns library guides give — MLA: author,
 *   description of the document, date, repository, form of the item; APA: author, date,
 *   italic title with an identifier in parentheses and a description in square
 *   brackets, repository. The issuing office stands as the author, as a government body
 *   does for any other document. Conventions, not published models.
 */

export const VITAL_FIELDS = {
  recordType: "recordType",
  subjects: "subjects",
  eventDate: "eventDate",
  number: "number",
  office: "office",
  copy: "copy",
  holder: "holder",
  censusYear: "censusYear",
  state: "state",
  county: "county",
  locality: "locality",
  ed: "ed",
  pageKind: "pageKind",
  page: "page",
  dwelling: "dwelling",
  family: "family",
  household: "household",
  line: "line",
  person: "person",
  microfilm: "microfilm",
  roll: "roll",
  database: "database",
  url: "url",
  accessed: "accessed",
} as const;

const F = VITAL_FIELDS;

/** A certificate or register entry, or a census schedule. */
export type VitalKind = "certificate" | "census";

const RECORD_TYPES = ["birth", "death", "marriage"] as const;
export type RecordType = (typeof RECORD_TYPES)[number];

const PAGE_KINDS = ["sheet", "stamped", "penned"] as const;
export type PageKind = (typeof PAGE_KINDS)[number];

export interface VitalLabels extends MediaLabels {
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Certificate {
  type: RecordType;
  subjects: PersonName[];
  date: PartialDate | null;
  number: string;
  office: string;
  copy: "certified" | "photocopy" | "digital";
  holder: string;
  database: string;
  url: string;
  accessed: PartialDate | null;
}

interface Census {
  year: string;
  state: string;
  county: string;
  locality: string;
  ed: string;
  pageKind: PageKind;
  page: string;
  dwelling: string;
  family: string;
  household: string;
  line: string;
  person: string;
  microfilm: string;
  roll: string;
  database: string;
  url: string;
  accessed: PartialDate | null;
}

function pick<T extends string>(
  options: readonly T[],
  text: string,
  fallback: T
): T {
  return options.find((option) => option === text) ?? fallback;
}

function readCertificate(values: MediaValues): Certificate {
  const copy = get(values, F.copy);
  return {
    type: pick(RECORD_TYPES, get(values, F.recordType), "birth"),
    subjects: parsePeople(get(values, F.subjects)),
    date: readDate(get(values, F.eventDate)),
    number: get(values, F.number),
    office: get(values, F.office),
    copy: copy === "photocopy" || copy === "digital" ? copy : "certified",
    holder: get(values, F.holder),
    database: get(values, F.database),
    url: fullUrl(get(values, F.url)),
    accessed: readDate(get(values, F.accessed)),
  };
}

function readCensus(values: MediaValues): Census {
  return {
    year: get(values, F.censusYear),
    state: get(values, F.state),
    county: get(values, F.county),
    locality: get(values, F.locality),
    ed: get(values, F.ed),
    pageKind: pick(PAGE_KINDS, get(values, F.pageKind), "sheet"),
    page: get(values, F.page),
    dwelling: get(values, F.dwelling),
    family: get(values, F.family),
    household: get(values, F.household),
    line: get(values, F.line),
    person: get(values, F.person),
    microfilm: get(values, F.microfilm),
    roll: get(values, F.roll),
    database: get(values, F.database),
    url: fullUrl(get(values, F.url)),
    accessed: readDate(get(values, F.accessed)),
  };
}

function capitalized(text: string): string {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

function subjectNames(subjects: PersonName[]): string {
  return andList(subjects.map(naturalName));
}

function join(parts: readonly string[], separator = ", "): string {
  return parts.filter(Boolean).join(separator);
}

function yearOf(date: PartialDate | null): string {
  return date ? String(date.year) : "n.d.";
}

/* ----------------------------------------------------------- certificates */

const RE_IN_PHRASE = /^in\s/i;

const COPY_KIND = {
  certified: "certified copy",
  photocopy: "photocopy",
  digital: "digital image",
} as const;

/** Chicago: how the copy in hand is described at the end of a note. */
function chicagoCopy(cert: Certificate): string {
  if (cert.copy === "digital") {
    const link = join([
      cert.database,
      cert.accessed ? `accessed ${longDate(cert.accessed)}` : "",
      cert.url,
    ]);
    return join([COPY_KIND.digital, link]);
  }
  const kind = COPY_KIND[cert.copy];
  if (!cert.holder) {
    return `${kind} in author's possession`;
  }
  return RE_IN_PHRASE.test(cert.holder)
    ? `${kind} ${cert.holder}`
    : `${kind}, ${cert.holder}`;
}

function certNote(cert: Certificate): CitationParts {
  const names = subjectNames(cert.subjects);
  const label = `${cert.type} certificate`;
  const lead = names ? `${names}, ${label}` : capitalized(label);
  const date = cert.date ? longDate(cert.date) : "";
  return [
    plain(
      `${join([lead, date, cert.number, cert.office, chicagoCopy(cert)])}.`
    ),
  ];
}

function certShortNote(cert: Certificate): CitationParts {
  const names = chicagoShortAuthors(cert.subjects);
  const label = `${cert.type} certificate`;
  return [plain(`${names ? `${names}, ${label}` : capitalized(label)}.`)];
}

function certBibliography(cert: Certificate): CitationParts {
  const names = chicagoBibliographyAuthors(cert.subjects);
  const label = capitalized(`${cert.type} certificate`);
  const lead = names ? `${names}${closer(names)} ` : "";
  const title = join([label, cert.number]);
  const date = cert.date ? `${longDate(cert.date)}.` : "";
  return tidyParts([
    plain(
      `${lead}${withPeriod(title)} ${join(
        [
          withPeriod(cert.office),
          date,
          withPeriod(capitalized(chicagoCopy(cert))),
        ],
        " "
      )}`
    ),
  ]);
}

function certTitle(cert: Certificate, titleCase: boolean): string {
  const names = subjectNames(cert.subjects);
  const label = titleCase
    ? `${capitalized(cert.type)} Certificate`
    : capitalized(`${cert.type} certificate`);
  return names ? `${label} of ${names}` : label;
}

function certMla(cert: Certificate): CitationParts {
  const office = cert.office ? `${withPeriod(cert.office)} ` : "";
  const tail = join([cert.date ? mlaDate(cert.date) : "", cert.number]);
  const parts: CitationParts = [
    plain(`${office}${withPeriod(certTitle(cert, true))}`),
  ];
  if (tail) {
    parts.push(plain(` ${tail}.`));
  }
  if (cert.copy !== "digital") {
    const held = cert.holder ? ` ${cert.holder}` : "";
    parts.push(plain(` ${capitalized(COPY_KIND[cert.copy])}${held}.`));
    return tidyParts(parts);
  }
  parts.push(plain(" "));
  if (cert.database) {
    parts.push(italic(cert.database));
  }
  parts.push(plain(`${cert.url ? `, ${mlaUrl(cert.url)}` : ""}.`));
  if (cert.accessed) {
    parts.push(plain(` Accessed ${mlaDate(cert.accessed)}.`));
  }
  return tidyParts(parts);
}

function certApa(cert: Certificate): CitationParts {
  const year = yearOf(cert.date);
  const number = cert.number ? ` (${capitalized(cert.number)})` : "";
  const description = ` [${capitalized(COPY_KIND[cert.copy])} of ${cert.type} certificate].`;
  const source =
    cert.copy === "digital"
      ? join([cert.database ? withPeriod(cert.database) : "", cert.url], " ")
      : "";
  const lead = cert.office
    ? `${withPeriod(cert.office)} (${year}). `
    : `(${year}). `;
  return tidyParts([
    plain(lead),
    italic(certTitle(cert, false)),
    plain(`${number}${description}${source ? ` ${source}` : ""}`),
  ]);
}

function certOutputs(cert: Certificate, labels: VitalLabels): CitationOutput[] {
  const office = cert.office || certTitle(cert, false);
  const year = yearOf(cert.date);
  return [
    {
      key: "chicago",
      label: labels.chicago,
      entries: [
        { label: labels.footnote, parts: certNote(cert) },
        { label: labels.shortNote, parts: certShortNote(cert) },
        { label: labels.bibliography, parts: certBibliography(cert) },
      ],
      note: labels.chicagoNote,
    },
    {
      key: "mla",
      label: labels.mla,
      entries: [
        { label: labels.worksCited, parts: certMla(cert) },
        { label: labels.inText, parts: [plain(`(${office})`)] },
      ],
      note: labels.mlaNote,
    },
    {
      key: "apa",
      label: labels.apa,
      entries: [
        { label: labels.referenceList, parts: certApa(cert) },
        {
          label: labels.parenthetical,
          parts: [plain(`(${office}, ${year})`)],
        },
        { label: labels.narrative, parts: [plain(`${office} (${year})`)] },
      ],
      note: labels.apaNote,
    },
  ];
}

/* ------------------------------------------------------------------ census */

function pageText(census: Census, short: boolean): string {
  if (!census.page) {
    return "";
  }
  if (census.pageKind === "sheet") {
    return `sheet ${census.page}`;
  }
  const word = short ? "p." : "page";
  return `${word} ${census.page} (${census.pageKind})`;
}

function households(census: Census, short: boolean): string {
  const parts = [
    census.dwelling
      ? `${short ? "dwell." : "dwelling"} ${census.dwelling}`
      : "",
    census.family ? `${short ? "fam." : "family"} ${census.family}` : "",
    census.household ? `household ${census.household}` : "",
    census.line ? `line ${census.line}` : "",
  ];
  return join(parts);
}

function censusTitle(census: Census): string {
  return `${census.year} U.S. census`;
}

function censusPlace(census: Census): string {
  return join([census.county, census.state, census.locality]);
}

function censusNote(census: Census): CitationParts {
  const front = join([
    censusTitle(census),
    censusPlace(census),
    "population schedule",
    census.ed ? `enumeration district (ED) ${census.ed}` : "",
    pageText(census, false),
    households(census, false),
    census.person,
  ]);
  const image = join([
    census.database,
    census.accessed ? `accessed ${longDate(census.accessed)}` : "",
    census.url,
  ]);
  const citing =
    census.microfilm || census.roll
      ? `citing NARA microfilm publication ${join([
          census.microfilm,
          census.roll ? `roll ${census.roll}` : "",
        ])}`
      : "";
  const digital = image ? `digital image, ${image}` : "";
  return [plain(`${join([front, join([digital, citing], "; ")], "; ")}.`)];
}

function censusShortNote(census: Census): CitationParts {
  const text = join([
    censusTitle(census),
    join([census.county, census.state]),
    "pop. sch.",
    pageText(census, true),
    households(census, true),
    census.person,
  ]);
  return [plain(`${text}.`)];
}

function censusBibliography(census: Census): CitationParts {
  const lead = join(
    [
      census.state ? withPeriod(census.state) : "",
      census.county ? withPeriod(census.county) : "",
    ],
    " "
  );
  const title = `${censusTitle(census)}, population schedule.`;
  const repository = census.database
    ? `Digital images. ${withPeriod(census.database)}`
    : "";
  const link = census.url ? withPeriod(census.url) : "";
  return [plain(join([lead, title, repository, link], " "))];
}

function censusMlaTitle(census: Census): string {
  const detail = join([
    census.county,
    census.state,
    census.locality,
    "Population Schedule",
    census.ed ? `Enumeration District ${census.ed}` : "",
    census.page
      ? `${census.pageKind === "sheet" ? "Sheet" : "Page"} ${census.page}`
      : "",
    census.household ? `Household ${census.household}` : "",
  ]);
  return `${census.year} United States Federal Census: ${detail}`;
}

function censusMla(census: Census): CitationParts {
  const parts: CitationParts = [
    plain(`United States, Bureau of the Census. “${censusMlaTitle(census)}.” `),
  ];
  if (census.database) {
    parts.push(italic(census.database));
  }
  const link = census.url ? mlaUrl(census.url) : "";
  parts.push(plain(`${link ? `, ${link}` : ""}.`));
  if (census.accessed) {
    parts.push(plain(` Accessed ${mlaDate(census.accessed)}.`));
  }
  return tidyParts(parts);
}

function censusApa(census: Census): CitationParts {
  const title = `${census.year} U.S. census: Population schedule, ${join([
    census.county,
    census.state,
    census.locality,
    census.ed ? `enumeration district ${census.ed}` : "",
    census.page
      ? `${census.pageKind === "sheet" ? "sheet" : "page"} ${census.page}`
      : "",
    census.household ? `household ${census.household}` : "",
  ])}`;
  const source = join(
    [census.database ? withPeriod(census.database) : "", census.url],
    " "
  );
  return tidyParts([
    plain(`U.S. Census Bureau. (${census.year || "n.d."}). `),
    italic(title),
    plain(` [Census record].${source ? ` ${source}` : ""}`),
  ]);
}

function censusOutputs(census: Census, labels: VitalLabels): CitationOutput[] {
  const year = census.year || "n.d.";
  return [
    {
      key: "chicago",
      label: labels.chicago,
      entries: [
        { label: labels.footnote, parts: censusNote(census) },
        { label: labels.shortNote, parts: censusShortNote(census) },
        { label: labels.bibliography, parts: censusBibliography(census) },
      ],
      note: labels.chicagoNote,
    },
    {
      key: "mla",
      label: labels.mla,
      entries: [
        { label: labels.worksCited, parts: censusMla(census) },
        {
          label: labels.inText,
          parts: [plain("(United States, Bureau of the Census)")],
        },
      ],
      note: labels.mlaNote,
    },
    {
      key: "apa",
      label: labels.apa,
      entries: [
        { label: labels.referenceList, parts: censusApa(census) },
        {
          label: labels.parenthetical,
          parts: [plain(`(U.S. Census Bureau, ${year})`)],
        },
        {
          label: labels.narrative,
          parts: [plain(`U.S. Census Bureau (${year})`)],
        },
      ],
      note: labels.apaNote,
    },
  ];
}

export function vitalIsReady(kind: VitalKind, values: MediaValues): boolean {
  if (kind === "census") {
    return get(values, F.censusYear) !== "";
  }
  return get(values, F.subjects) !== "" || get(values, F.office) !== "";
}

export function formatVitalRecord(
  kind: VitalKind,
  values: MediaValues,
  labels: VitalLabels
): CitationOutput[] {
  if (kind === "census") {
    return censusOutputs(readCensus(values), labels);
  }
  return certOutputs(readCertificate(values), labels);
}
